import { useState } from 'react';
import { initials } from '@/utils';

interface AvatarProps {
  name: string | null | undefined;
  /** Secondary identity used for the initials fallback (e.g. an email). */
  email?: string | null;
  /** Signed URL for the photo; when absent (or it fails to load) we fall back to initials. */
  photoUrl?: string | null;
  /** Rendered square size in pixels. Default 40. */
  size?: number;
  className?: string;
}

/**
 * Generic round avatar: photo when a signed URL is available, otherwise the
 * green initials bubble. Shared by admin surfaces (via <AdminAvatar>) and coach
 * pages.
 *
 * Stored photos are bounded (not cropped) on upload, so object-cover guarantees
 * a clean round crop at any render size.
 */
export default function Avatar({ name, email, photoUrl, size = 40, className = '' }: AvatarProps) {
  // Track the specific URL that failed to load — NOT a latching boolean. Hooks
  // re-sign photos (~hourly, new token → new photoUrl); a bool would keep showing
  // initials for the rest of a long-lived session (e.g. the PIN kiosk) after one
  // transient load error. Keying on the URL means a fresh URL is retried.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const dimension = { width: size, height: size };
  // Scale the initials text to roughly 40% of the bubble, matching the original
  // h-10/w-10 bubble's text-sm.
  const fontSize = Math.round(size * 0.4);

  if (photoUrl && failedUrl !== photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || email || 'Photo'}
        onError={() => setFailedUrl(photoUrl)}
        style={dimension}
        className={`shrink-0 rounded-full object-cover bg-gray-100 ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{ ...dimension, fontSize }}
      className={`shrink-0 inline-flex items-center justify-center rounded-full bg-green-100 text-green-800 font-semibold ${className}`}
    >
      {initials(name, email ?? null)}
    </span>
  );
}
